import { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Dropdown } from '@shopflo/ui'
import { DataFilterColumnOptionType } from 'src/lib/types/filters'
import { Schedule } from 'src/components/common/export-component/types'
import { Info } from '@phosphor-icons/react'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import TextField from '@mui/material/TextField'
import { defaultErrorState } from 'src/assets/DiscountsConfig'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import { format, differenceInDays } from 'date-fns'
import DatePicker from 'src/components/common/calendar/DatePicker'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectScrollUpButton,
  SelectScrollDownButton,
  Icon
} from '@shopflo/ui'

interface RecurringProps {
  schedule?: Schedule
  onChange: (schedule: Schedule) => void
  isEdit?: boolean
}

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`
}))

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
]

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`
}))

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const frequencyOptions: DataFilterColumnOptionType[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' }
]

export const generateCronExpression = ({
  frequency,
  hour,
  minute = 0,
  day
}: {
  frequency: 'daily' | 'weekly' | 'monthly' | 'hourly'
  hour: number
  minute?: number
  day: number
}) => {
  const minuteStr = minute.toString()
  let dayOfMonth = '*'
  const month = '*'
  let dayOfWeek = '?'
  const year = '*'

  switch (frequency) {
    case 'hourly':
      return `* ${hour} * * * *`
    case 'daily':
      dayOfWeek = '?'
      dayOfMonth = '*'
      break
    case 'weekly':
      dayOfMonth = '?'
      dayOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'][day - 1]
      break
    case 'monthly':
      dayOfWeek = '?'
      dayOfMonth = day.toString()
      break
  }

  return `${minuteStr} ${hour} ${dayOfMonth} ${month} ${dayOfWeek} ${year}`
}

export const parseCronExpression = (cronExpression: string): Pick<Schedule, 'frequency' | 'hour' | 'day'> => {
  const parts = cronExpression.split(' ')

  // Hourly pattern: "* H * * * *"
  if (parts[0] === '*' && parts.length === 6) {
    const hour = parseInt(parts[1])
    return { frequency: 'hourly', hour }
  }

  const hour = parseInt(parts[1])
  const dayOfMonth = parts[2]
  let dayOfWeek = parts[4]

  const shortDaysInAWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

  if (shortDaysInAWeek.some(item => cronExpression.includes(item))) {
    dayOfWeek = (shortDaysInAWeek.indexOf(dayOfWeek) + 1).toString()
  }

  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return { frequency: 'daily', hour, day: undefined }
  } else if (dayOfMonth === '*' && dayOfWeek !== '*') {
    return { frequency: 'weekly', hour, day: parseInt(dayOfWeek) }
  } else {
    return { frequency: 'monthly', hour, day: parseInt(dayOfMonth) }
  }
}

const initialFormErrorState = {
  startDate: { status: false, errorCode: '' }
}

export default function Recurring({ schedule, onChange, isEdit = false }: RecurringProps) {
  const initialValues = schedule?.cronExpression
    ? parseCronExpression(schedule.cronExpression.replace('?', '*'))
    : { frequency: schedule?.frequency || 'daily', hour: schedule?.hour || 10, day: schedule?.day || 1 }

  const [frequency, setFrequency] = useState<Schedule['frequency']>(initialValues.frequency)
  const [selectedHour, setSelectedHour] = useState<number>(initialValues.hour)
  const [selectedDay, setSelectedDay] = useState<number>(initialValues.day || 1)
  const [startDate, setStartDate] = useState<string | Date>(schedule?.scheduledStartDate || new Date())
  const [formErrors, setFormErrors] = useState(initialFormErrorState)

  const getScheduleDescription = () => {
    switch (frequency) {
      case 'daily':
        return 'Exports data for the previous calendar day.'
      case 'weekly':
        return 'Exports data for the last 7 days'
      case 'monthly':
        return 'Exports data for the last 31 days'
    }
  }

  const cronExpression = useMemo(
    () => generateCronExpression({ frequency, day: Number(selectedDay), hour: selectedHour }),
    [frequency, selectedDay, selectedHour]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div className='flex gap-6 w-full items-end'>
        <div className='flex-1'>
          <div className='flex items-end gap-2'>
            <div className='flex-1 flex flex-col'>
              <label className='text-text-xs-medium text-zinc-500 mb-1'>Frequency</label>
              <Select
                value={frequency}
                onValueChange={value => {
                  setFrequency(value as Schedule['frequency'])
                  onChange({
                    frequency: value as Schedule['frequency'],
                    hour: selectedHour,
                    day: frequency !== 'daily' ? selectedDay : undefined,
                    scheduledStartDate: startDate.toString(),
                    cronExpression: generateCronExpression({
                      frequency: value as Schedule['frequency'],
                      hour: selectedHour,
                      day: frequency !== 'daily' ? selectedDay : 1
                    })
                  })
                }}
              >
                <SelectTrigger className='w-full h-9 rounded-lg'>
                  <SelectValue placeholder='Select Frequency' />
                </SelectTrigger>
                <SelectContent className='ui-max-h-52'>
                  <SelectScrollUpButton />
                  {frequencyOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  <SelectScrollDownButton />
                </SelectContent>
              </Select>
            </div>

            {frequency !== 'daily' && (
              <div className='flex-1 flex flex-col'>
                <label className='text-text-xs-medium text-zinc-500 mb-1'>Day</label>
                {frequency === 'monthly' && (
                  <Select
                    value={selectedDay.toString()}
                    onValueChange={value => {
                      const day = Number(value)
                      setSelectedDay(day)
                      onChange({
                        frequency,
                        hour: selectedHour,
                        day,
                        scheduledStartDate: startDate.toString(),
                        cronExpression: generateCronExpression({ frequency, hour: selectedHour, day })
                      })
                    }}
                  >
                    <SelectTrigger className='w-full h-9 rounded-lg'>
                      <SelectValue placeholder='Select Day' />
                    </SelectTrigger>
                    <SelectContent className='ui-max-h-52'>
                      <SelectScrollUpButton />
                      {DAYS_OF_MONTH.map(option => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                      <SelectScrollDownButton />
                    </SelectContent>
                  </Select>
                )}
                {frequency === 'weekly' && (
                  <Select
                    value={selectedDay.toString()}
                    onValueChange={value => {
                      const day = Number(value)
                      setSelectedDay(day)
                      onChange({
                        frequency,
                        day,
                        hour: selectedHour,
                        scheduledStartDate: startDate.toString(),
                        cronExpression: generateCronExpression({ frequency, hour: selectedHour, day })
                      })
                    }}
                  >
                    <SelectTrigger className='w-full h-9 rounded-lg'>
                      <SelectValue placeholder='Select Day' />
                    </SelectTrigger>
                    <SelectContent className='ui-max-h-52'>
                      <SelectScrollUpButton />
                      {DAYS_OF_WEEK.map(option => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                      <SelectScrollDownButton />
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className='flex-1 flex flex-col'>
              <label className='text-text-xs-medium text-zinc-500 mb-1'>Time</label>
              <Select
                value={selectedHour.toString()}
                onValueChange={value => {
                  const hour = Number(value)
                  setSelectedHour(hour)
                  onChange({
                    frequency,
                    hour,
                    day: frequency !== 'daily' ? selectedDay : undefined,
                    scheduledStartDate: startDate.toString(),
                    cronExpression: generateCronExpression({
                      frequency,
                      hour,
                      day: frequency !== 'daily' ? selectedDay ?? 1 : 1
                    })
                  })
                }}
              >
                <SelectTrigger className='w-full h-9 rounded-lg'>
                  <SelectValue placeholder='Select Hour' />
                </SelectTrigger>
                <SelectContent className='ui-max-h-52'>
                  <SelectScrollUpButton />
                  {HOURS.map(option => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                  <SelectScrollDownButton />
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {!isEdit && (
          <div className='flex-1 flex flex-col gap-px'>
            <h2 className='text-text-xs-medium text-zinc-500 mb-1'>Start on:</h2>
            <div className='[&_button]:!w-full'>
              <DatePicker
                date={startDate}
                onChange={newValue => {
                  setStartDate(newValue ?? '')
                  onChange({
                    frequency,
                    hour: selectedHour,
                    day: frequency !== 'daily' ? selectedDay : undefined,
                    scheduledStartDate: newValue?.toString() ?? '',
                    cronExpression
                  })
                  if (newValue && differenceInDays(new Date(newValue), new Date()) < 0) {
                    setFormErrors(oldValues => ({
                      ...oldValues,
                      startDate: { status: true, errorCode: 'DC_FORM_017' }
                    }))
                  } else {
                    setFormErrors(oldValues => ({ ...oldValues, startDate: defaultErrorState }))
                  }
                }}
                disablePastDates
              />
            </div>
          </div>
        )}
      </div>

      {!isEdit && formErrors?.startDate?.status && (
        <TextFieldError message={errorMappings?.[formErrors.startDate.errorCode as keyof typeof errorMappings]} />
      )}

      <div className='flex items-center gap-1 pb-2'>
        <Icon IconComponent={Info} size='xs' iconProps={{ className: 'text-zinc-500', weight: 'duotone' }} />
        <span className='text-text-xs-medium text-zinc-600'>{getScheduleDescription()}</span>
      </div>
    </Box>
  )
}
